import {
  Award,
  Car,
  Clock,
  Crown,
  Gift,
  Heart,
  LogOut,
  Mail, Phone,
  Shield,
  User
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import CarImage from '../components/CarImage';
import { useAuth } from '../contexts/AuthContext';
import { useAuthDialog } from '../contexts/AuthDialogContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { useLanguage } from '../contexts/LanguageContext';
import odooApi from '../services/odooApi';

const PROFILE_TABS = ['overview', 'favorites', 'loyalty'];

const ProfilePage = () => {
  const { t } = useLanguage();
  const { user, logout, isAuthenticated } = useAuth();
  const { openAuth } = useAuthDialog();
  const { formatPrice } = useCurrency();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [activeTab, setActiveTab] = useState('overview');
  const [loyaltyInfo, setLoyaltyInfo] = useState(null);
  const [loyaltyHistory, setLoyaltyHistory] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated) {
      loadData();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const requestedTab = searchParams.get('tab');
    if (requestedTab && PROFILE_TABS.includes(requestedTab)) {
      setActiveTab(requestedTab);
      return;
    }
    setActiveTab('overview');
  }, [searchParams]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [loyalty, history, userFavorites] = await Promise.all([
        odooApi.getLoyaltyInfo(),
        odooApi.getLoyaltyHistory(),
        odooApi.getFavorites(),
      ]);
      setLoyaltyInfo(loyalty);
      setLoyaltyHistory(history);
      setFavorites(userFavorites);
    } catch (e) {
      console.error('Error loading profile data:', e);
    }
    setLoading(false);
  };

  const getTierColor = (tier) => {
    const colors = {
      bronze: 'from-amber-700 to-amber-900',
      silver: 'from-gray-400 to-gray-600',
      gold: 'from-yellow-400 to-yellow-600',
      platinum: 'from-purple-400 to-purple-600',
    };
    return colors[tier] || colors.bronze;
  };

  const getTierIcon = (tier) => {
    return <Crown className={`w-6 h-6 ${tier === 'platinum' ? 'text-purple-400' : tier === 'gold' ? 'text-yellow-400' : tier === 'silver' ? 'text-gray-400' : 'text-amber-700'}`} />;
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    const nextParams = new URLSearchParams(searchParams);
    if (tabId === 'overview') {
      nextParams.delete('tab');
    } else {
      nextParams.set('tab', tabId);
    }
    setSearchParams(nextParams, { replace: true });
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <User className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">{t('profile.signInTitle', 'Please Sign In')}</h2>
          <p className="text-gray-400">{t('profile.signInDescription', 'Sign in to view your profile')}</p>
          <button
            onClick={() => openAuth('login')}
            className="mt-6 rounded-lg bg-[#D4AF37] px-5 py-3 font-semibold text-black"
          >
            {t('auth.signIn', 'Sign In')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white py-12">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Profile Header */}
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-8 mb-8">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="w-24 h-24 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#B8860B] flex items-center justify-center text-3xl font-bold text-black">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1 text-center md:text-left">
              <h1 className="text-3xl font-bold mb-2">{user?.name}</h1>
              <div className="flex flex-wrap justify-center md:justify-start gap-4 text-gray-400">
                {user?.email && (
                  <span className="flex items-center gap-2">
                    <Mail className="w-4 h-4" /> {user.email}
                  </span>
                )}
                {user?.phone && (
                  <span className="flex items-center gap-2">
                    <Phone className="w-4 h-4" /> {user.phone}
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={logout}
              className="flex items-center gap-2 px-4 py-2 bg-red-600/20 text-red-400 rounded-lg hover:bg-red-600/30 transition-colors"
            >
              <LogOut className="w-4 h-4" /> {t('auth.signOut', 'Sign Out')}
            </button>
          </div>
        </div>

        {/* Loyalty Card */}
        {loyaltyInfo && (
          <div className={`bg-gradient-to-r ${getTierColor(loyaltyInfo.tier)} rounded-2xl p-6 mb-8 relative overflow-hidden`}>
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  {getTierIcon(loyaltyInfo.tier)}
                  <span className="text-xl font-bold capitalize">
                    {t(`loyalty.tier.${loyaltyInfo.tier}`, loyaltyInfo.tier)} {t('profile.member', 'Member')}
                  </span>
                </div>
                <Gift className="w-8 h-8 opacity-50" />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-white/60 text-sm">{t('profile.pointsBalance', 'Points Balance')}</p>
                  <p className="text-2xl font-bold">{loyaltyInfo.points?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-white/60 text-sm">{t('favorites.title', 'Favorites')}</p>
                  <p className="text-2xl font-bold">{favorites.length}</p>
                </div>
                <div>
                  <p className="text-white/60 text-sm">{t('profile.totalSpent', 'Total Spent')}</p>
                  <p className="text-2xl font-bold">{formatPrice(loyaltyInfo.totalSpent)}</p>
                </div>
                <div>
                  <p className="text-white/60 text-sm">{t('common.discount', 'Discount')}</p>
                  <p className="text-2xl font-bold">{loyaltyInfo.tierBenefits?.discount || 0}%</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {[
            { id: 'overview', label: t('profile.overview', 'Overview'), icon: User },
            { id: 'favorites', label: t('favorites.title', 'Favorites'), icon: Heart },
            { id: 'loyalty', label: t('profile.loyaltyRewards', 'Loyalty'), icon: Award },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'bg-[#D4AF37] text-black'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-[#D4AF37]" />
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="grid gap-6 md:grid-cols-2">
                <div className="bg-gray-900 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Car className="w-6 h-6 text-[#D4AF37]" />
                    <h3 className="text-lg font-semibold">{t('profile.startExploring', 'Start exploring our premium cars!')}</h3>
                  </div>
                  <p className="text-sm text-gray-400 leading-relaxed">
                    {t('profile.browseCollection', 'Discover the latest arrivals, exclusive deals, and premium brands curated for your next drive.')}
                  </p>
                  <div className="mt-6 grid grid-cols-2 gap-3">
                    <Link
                      to="/cars"
                      className="btn-gold rounded-lg px-4 py-3 text-center text-sm font-semibold text-black"
                    >
                      {t('btn.browse', 'Browse Cars')}
                    </Link>
                    <Link
                      to="/contact"
                      className="rounded-lg border border-[#D4AF37]/40 px-4 py-3 text-center text-sm font-semibold text-[#D4AF37] transition-colors hover:bg-[#D4AF37] hover:text-black"
                    >
                      {t('btn.contactUs', 'Contact Us')}
                    </Link>
                  </div>
                </div>

                <div className="bg-gray-900 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Heart className="w-6 h-6 text-[#D4AF37]" />
                    <h3 className="text-lg font-semibold">{t('favorites.title', 'Favorites')}</h3>
                  </div>
                  {favorites.slice(0, 3).map(car => (
                    <div key={car.id} className="flex items-center gap-3 py-3 border-b border-gray-800 last:border-0">
                      <div className="h-12 w-16 overflow-hidden rounded">
                        <CarImage
                          src={car.image}
                          alt={car.name}
                          iconClassName="h-5 w-5"
                        />
                      </div>
                      <div>
                        <p className="font-medium">{car.name}</p>
                        <p className="text-sm text-[#D4AF37]">
                          {formatPrice(car.pricePerDay)}/{t('common.day', 'day')}
                        </p>
                      </div>
                    </div>
                  ))}
                  {favorites.length === 0 && (
                    <p className="text-gray-500 text-center py-4">{t('favorites.emptyTitle', 'No favorites yet')}</p>
                  )}
                </div>
              </div>
            )}

            {/* Favorites Tab */}
            {activeTab === 'favorites' && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {favorites.map(car => (
                  <div key={car.id} className="bg-gray-900 rounded-xl overflow-hidden group">
                    <div className="relative h-48">
                      <div className="h-full w-full transition-transform duration-300 group-hover:scale-105">
                        <CarImage
                          src={car.image}
                          alt={car.name}
                        />
                      </div>
                      <div className="absolute top-4 right-4">
                        <Heart className="w-6 h-6 text-red-500 fill-red-500" />
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-bold text-lg mb-1">{car.name}</h3>
                      <p className="text-gray-400 text-sm mb-3">{car.category}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-[#D4AF37] font-bold">
                          {formatPrice(car.pricePerDay)}/{t('common.day', 'day')}
                        </span>
                        <a 
                          href={`/cars/${car.id}`}
                          className="px-4 py-2 bg-[#D4AF37] text-black rounded-lg text-sm font-medium hover:bg-[#B8860B] transition-colors"
                        >
                          {t('btn.details', 'View Details')}
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
                {favorites.length === 0 && (
                  <div className="col-span-full text-center py-20 bg-gray-900 rounded-xl">
                    <Heart className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                    <h3 className="text-xl font-bold mb-2">{t('favorites.emptyTitle', 'No Favorites Yet')}</h3>
                    <p className="text-gray-400">{t('profile.saveCars', 'Save cars you love for quick access!')}</p>
                  </div>
                )}
              </div>
            )}

            {/* Loyalty Tab */}
            {activeTab === 'loyalty' && (
              <div className="grid md:grid-cols-3 gap-6">
                {/* Tier Benefits */}
                <div className="md:col-span-2 bg-gray-900 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                    <Shield className="w-6 h-6 text-[#D4AF37]" />
                    {t('profile.tierBenefits', 'Tier Benefits')}
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {[
                      { tier: 'bronze', points: '0+', discount: '0%', benefits: ['Basic access', 'Email support'] },
                      { tier: 'silver', points: '2,000+', discount: '5%', benefits: ['Priority support', 'Exclusive offers', 'Early access'] },
                      { tier: 'gold', points: '5,000+', discount: '10%', benefits: ['Free upgrades', 'VIP support', 'Special rates'] },
                      { tier: 'platinum', points: '10,000+', discount: '15%', benefits: ['Airport pickup', 'Personal concierge', 'All benefits'] },
                    ].map(t => (
                      <div 
                        key={t.tier}
                        className={`p-4 rounded-lg border-2 ${
                          loyaltyInfo?.tier === t.tier 
                            ? 'border-[#D4AF37] bg-[#D4AF37]/10' 
                            : 'border-gray-700 bg-gray-800'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-bold capitalize">{t.tier}</span>
                          <span className="text-sm text-gray-400">{t.points} pts</span>
                        </div>
                        <p className="text-[#D4AF37] font-bold mb-2">{t.discount} discount</p>
                        <ul className="text-sm text-gray-400 space-y-1">
                          {t.benefits.map((b, i) => (
                            <li key={i}>• {b}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Points History */}
                <div className="bg-gray-900 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                    <Clock className="w-6 h-6 text-[#D4AF37]" />
                    {t('profile.pointsHistory', 'Points History')}
                  </h3>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {loyaltyHistory.map(item => (
                      <div key={item.id} className="flex items-center justify-between py-2 border-b border-gray-800 last:border-0">
                        <div>
                          <p className="text-sm">{item.description}</p>
                          <p className="text-xs text-gray-500">{new Date(item.date).toLocaleDateString()}</p>
                        </div>
                        <span className={`font-bold ${item.points > 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {item.points > 0 ? '+' : ''}{item.points}
                        </span>
                      </div>
                    ))}
                    {loyaltyHistory.length === 0 && (
                      <p className="text-gray-500 text-center py-4">{t('profile.noHistory', 'No history yet')}</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
